<template>
  <div class="container-fluid">
    <div class="block-header">
      <bread-crumb :breadCrumbIteams="breadCrumbIteams"></bread-crumb>
    </div>

    <div class="row clearfix">
      <div class="col-lg-12">
        <div class="card card-top-line">
          <div class="header offers-detail-header">
            <div>
              <h2>Offer Details</h2>
              <p class="text-muted mb-0">Review offer content, linked countries, and current status.</p>
            </div>
            <b-button variant="outline-secondary" @click="$router.push('/offers/list')">Back to List</b-button>
          </div>

          <div class="body pt-0">
            <div v-if="offer" class="offer-detail-shell row">
              <div class="col-lg-7">
                <div class="offer-detail-card">
                  <div class="offer-detail-card__header">
                    <h5 class="mb-0">Offer Information</h5>
                    <span class="offer-status-badge" :class="statusClass(offer.status)">
                      {{ offer.status || "-" }}
                    </span>
                  </div>

                  <div class="offer-detail-grid">
                    <div class="offer-detail-item">
                      <span class="offer-detail-item__label">Business</span>
                      <strong>{{ offer.business_name || "-" }}</strong>
                    </div>
                    <div class="offer-detail-item">
                      <span class="offer-detail-item__label">Offer Type</span>
                      <span class="offer-type-chip">{{ offer.offer_type || "-" }}</span>
                    </div>
                  </div>

                  <div class="offer-country-section">
                    <span class="offer-detail-item__label">Countries</span>
                    <div v-if="offerCountries.length" class="offer-country-list">
                      <span v-for="country in offerCountries" :key="country.countryid" class="offer-country-chip">
                        {{ country.countryname }}
                      </span>
                    </div>
                    <span v-else class="text-muted">-</span>
                  </div>
                </div>
              </div>

              <div class="col-lg-5">
                <div class="offer-detail-card offer-detail-card--media">
                  <div class="offer-detail-card__header">
                    <h5 class="mb-0">Offer Image</h5>
                  </div>
                  <div class="preview-wrapper">
                    <img
                      v-if="offer.offer_image && !imageError"
                      :src="offer.offer_image"
                      alt="Offer image"
                      class="offer-preview"
                      @error="imageError = true"
                    />
                    <div v-else class="preview-placeholder">
                      No image preview
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="text-center text-muted py-4">Offer not found.</div>
          </div>
        </div>
      </div>
    </div>

    <main-loader :loading="isLoading"></main-loader>
  </div>
</template>

<script>
import { GET_OFFER_DETAILS } from "@/store/actions.type";

const LoaderComponent = require("@/components/admin/loader/LoaderComponent.vue").default;
const BreadCrumb = require("@/components/BreadCrumb.vue").default;

export default {
  name: "OfferDetailComponent",
  components: {
    "main-loader": LoaderComponent,
    "bread-crumb": BreadCrumb,
  },
  computed: {
    offerCountries() {
      if (!this.offer) return [];
      if (Array.isArray(this.offer.countries) && this.offer.countries.length) {
        return this.offer.countries;
      }
      if (Array.isArray(this.offer.country_names)) {
        return this.offer.country_names.map((countryname, index) => ({
          countryid: this.offer.country_ids?.[index] || `${index}`,
          countryname,
        }));
      }
      return this.offer.country_name
        ? [{ countryid: this.offer.country_id || "single-country", countryname: this.offer.country_name }]
        : [];
    },
  },
  data() {
    return {
      breadCrumbIteams: [
        { title: "Offers", links: "/offers/list" },
        { title: "Offer Details", links: "" },
      ],
      isLoading: false,
      imageError: false,
      offer: null,
    };
  },
  mounted() {
    this.loadDetails();
  },
  methods: {
    statusClass(status) {
      return {
        "is-active": status === "active",
        "is-inactive": status === "inactive",
        "is-expired": status === "expired",
      };
    },
    async loadDetails() {
      this.isLoading = true;
      try {
        const response = await this.$store.dispatch(GET_OFFER_DETAILS, this.$route.params.id);
        this.offer = response?.data?.details || null;
        if (!this.offer) {
          this.$toast.warning("Offer not found.", { position: "top-right" });
        }
      } catch (error) {
        if (error?.response?.status === 404) {
          this.$toast.warning("Offer not found.", { position: "top-right" });
        } else {
          this.$toast.error(error?.response?.data?.message || "Failed to load offer details.", {
            position: "top-right",
          });
        }
        this.offer = null;
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.offers-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.offers-detail-header p {
  margin-top: 4px;
}

.offer-detail-shell {
  padding-top: 8px;
}

.offer-detail-card {
  height: 100%;
  border: 1px solid #e9ecef;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(26, 35, 67, 0.05);
  overflow: hidden;
}

.offer-detail-card__header {
  padding: 18px 20px;
  border-bottom: 1px solid #edf2f7;
  background: linear-gradient(180deg, #fcfdff 0%, #f8fafc 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.offer-detail-card--media .preview-wrapper {
  margin: 20px;
}

.offer-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  padding: 20px;
}

.offer-detail-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.offer-detail-item__label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #7a8698;
}

.preview-wrapper {
  min-height: 260px;
  border: 1px dashed #ced4da;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
}

.offer-country-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 20px 20px;
}

.offer-country-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 2px;
}

.offer-country-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: #f8fafc;
  color: #445063;
  font-weight: 600;
  font-size: 12px;
  border: 1px solid #e6edf5;
}

.offer-type-chip {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(71, 167, 184, 0.12);
  color: #157786;
  font-weight: 600;
  font-size: 12px;
}

.offer-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 88px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}

.offer-status-badge.is-active {
  background: rgba(111, 183, 63, 0.12);
  color: #4c8e22;
}

.offer-status-badge.is-inactive {
  background: rgba(108, 117, 125, 0.12);
  color: #5f6b7a;
}

.offer-status-badge.is-expired {
  background: rgba(216, 66, 77, 0.12);
  color: #b2343d;
}

.offer-preview {
  width: 100%;
  height: 260px;
  object-fit: contain;
  border-radius: 8px;
  background: #fff;
  padding: 16px;
}

.preview-placeholder {
  color: #6c757d;
  font-size: 13px;
  text-align: center;
  padding: 20px;
}

@media (max-width: 991px) {
  .offers-detail-header,
  .offer-detail-card__header {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 767px) {
  .offer-detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
